import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { SITE, IMAGES } from "../data/site";
import { useLang, LANGS } from "../i18n";

export const Logo = ({ light = false }) => {
  if (light) {
    return (
      <Link to="/" data-testid="logo-link" className="inline-block bg-white px-3 py-2">
        <img src={IMAGES.logoFull} alt="Sana Steel Industries" className="h-12 w-auto" />
      </Link>
    );
  }
  return (
    <Link to="/" data-testid="logo-link" className="flex items-center gap-3">
      <img src={IMAGES.logoMark} alt="Sana Steel Industries logo" className="h-12 w-auto" />
      <span className="leading-none hidden sm:block">
        <span className="block font-heading font-black uppercase tracking-tight text-lg text-navy">
          Sana Steel
        </span>
        <span className="block font-heading text-[10px] font-bold uppercase tracking-[0.35em] text-steel">
          Industries
        </span>
      </span>
    </Link>
  );
};

export const LangSwitcher = ({ dark = false }) => {
  const { lang, setLang } = useLang();
  return (
    <div className={`flex items-center border ${dark ? "border-white/30" : "border-gray-300"}`} data-testid="language-switcher">
      {LANGS.map((l) => (
        <button
          key={l.code}
          onClick={() => setLang(l.code)}
          data-testid={`lang-${l.code}`}
          className={`px-2.5 py-1.5 text-xs font-heading font-bold transition-colors duration-200 ${
            lang === l.code
              ? "bg-navy text-white"
              : dark
                ? "text-white/70 hover:text-white"
                : "text-navy hover:bg-surface"
          }`}
        >
          {l.label}
        </button>
      ))}
    </div>
  );
};

const PATHS = [
  { to: "/", key: "nav.home" },
  { to: "/about", key: "nav.about" },
  { to: "/products", key: "nav.products" },
  { to: "/contact", key: "nav.contact" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const { t } = useLang();

  useEffect(() => setOpen(false), [pathname]);

  return (
    <>
      <header className="fixed top-0 inset-x-0 z-50 bg-white/90 backdrop-blur-md border-b border-gray-200" data-testid="navbar">
        <div className="max-w-7xl mx-auto px-5 md:px-8 h-20 flex items-center justify-between">
          <Logo />
          <nav className="hidden md:flex items-center gap-6 lg:gap-8" data-testid="nav-links">
            {PATHS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.to === "/" ? "home" : l.to.slice(1)}-link`}
                className={({ isActive }) =>
                  `font-heading text-xs font-bold uppercase tracking-[0.2em] transition-colors duration-200 ${
                    isActive ? "text-steel" : "text-navy hover:text-steel"
                  }`
                }
              >
                {t(l.key)}
              </NavLink>
            ))}
            <LangSwitcher />
            <Link to="/quote" data-testid="nav-quote-button" className="btn-primary !px-6 !py-3">
              {t("nav.quote")}
            </Link>
          </nav>
          <div className="flex items-center gap-3 md:hidden">
            <LangSwitcher />
            <a href={SITE.phone1Href} data-testid="nav-call-button" aria-label="Call Sana Steel"
               className="w-10 h-10 border border-gray-300 flex items-center justify-center text-navy">
              <Phone size={18} />
            </a>
            <button onClick={() => setOpen(!open)} data-testid="nav-menu-toggle" aria-label="Toggle menu"
                    className="w-10 h-10 border border-gray-300 flex items-center justify-center text-navy">
              {open ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
      </header>
      {open && (
        <div className="md:hidden fixed inset-x-0 top-20 bottom-0 bg-navy z-40 flex flex-col justify-center px-8 overflow-y-auto"
             data-testid="mobile-menu">
          {PATHS.map((l, i) => (
            <NavLink key={l.to} to={l.to}
                     data-testid={`mobile-nav-${l.to === "/" ? "home" : l.to.slice(1)}-link`}
                     className="font-heading text-4xl font-black uppercase tracking-tight text-white py-4 border-b border-white/10">
              <span className="text-silver text-base align-top mr-3">0{i + 1}</span>
              {t(l.key)}
            </NavLink>
          ))}
          <Link to="/quote" data-testid="mobile-nav-quote-button" className="btn-light mt-8 self-start">
            {t("nav.quote")}
          </Link>
        </div>
      )}
    </>
  );
};

